import { FC, useState, useEffect } from "react";
import { Box, useMediaQuery, useTheme, Typography } from "@mui/material";
import Pagination from "src/Pages/Company/OrganizationStructure/sharedComponents/Pagination";
import Skeletons from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/Components/Skeletons";
import CreateAreaResult from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/Components/CreateAreaResult";
import AreaResult from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/Components/AreaResult";
import { styles } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/styles";
import useFetchAreas from "src/Pages/Company/OrganizationStructure/api/useFetchAreas";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import getPositionLabel from "src/Pages/Company/OrganizationStructure/utils/getPositionLabel";
import Eyes from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/Components/Eyes";
import __ from "src/utils/Translations";
import { IAreasQueryParams } from "./StructureAreas.models";

const Areas: FC<IAreasQueryParams> = ({ search, sortType, structureId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const companyId = useCompanyId();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [structureAvatar, setStructureAvatar] = useState<null | string>(null);
  const { data, isPending, isFetching } = useFetchAreas(
    companyId,
    structureId,
    currentPage,
    sortType,
    search
  );

  const { data: structureData } = useFetchStructureData(
    companyId,
    structureId!
  );

  const hasAnyArea = !!data?.content.length;

  useEffect(() => {
    if (structureData) {
      setStructureAvatar(structureData.avatarLink);
    }
  }, [isFetching, structureData]);

  if (isPending || isFetching) {
    return <Skeletons limit={10} isMobile={isMobile} />;
  }

  return (
    <>
      {hasAnyArea && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "fit-content",
              margin: "16px 0 16px auto",
            }}
          >
            <Typography fontSize="13px" mr={1} lineHeight="32px">
              {getPositionLabel(data.totalElements)}
            </Typography>
            {data.totalPages >= 2 && (
              <Pagination
                count={data.totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
              />
            )}
          </Box>
          <Box
            sx={{
              ...styles?.Areas?.areasContainer,
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
              marginTop: 2,
            }}
          >
            {data.content.map((item) => (
              <AreaResult
                areaId={item.areaId}
                name={item.name}
                structure={item.structure}
                manager={item.manager}
                membersNumber={item.membersNumber}
                structureAvatar={structureAvatar}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "fit-content",
              margin: "22px 0 22px auto",
            }}
          >
            {data.totalPages >= 2 && (
              <Pagination
                count={data.totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
              />
            )}
          </Box>
        </>
      )}
      {!hasAnyArea && search === "" && <CreateAreaResult />}
      {!hasAnyArea && search !== "" && (
        <Box
          sx={{
            margin: "30px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Eyes />
          <Typography
            sx={{ fontSize: "14px" }}
            color="textSecondary"
            fontWeight="fontWeightMedium"
          >
            {__("Brak wyników dla wyszukiwanej frazy")}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default Areas;
